/**
 * Двухэтажная шапка приложения.
 *
 * Первый этаж — навигация: одинаковый на всех экранах, включая консоль.
 * Второй — контекст текущего экрана: телеметрия выбранного дрона в консоли,
 * заголовок раздела на остальных. Экраны наполняют его через PageBar.
 *
 * Запрос /user/start живёт здесь один на всё приложение: раньше его дублировали
 * NavMenu и ConsoleNavMenu независимо друг от друга.
 */

import './AppHeader.css';
import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LogoUrl from '../../assets/icons/Logo';
import { UserSchema } from '../../api/User';
import { APIRequestGet } from '../../utils/APIRequests';
import { visibleSections } from '../../navigation/menu';
import { useAuth } from '../../AuthContext';
import { useAccess } from '../../access/PlatformAccess';
import UserActions from './UserActions';

interface AppHeaderProps {
    /** Сюда AppLayout забирает узел второго этажа для порталов. */
    tier2Ref: (node: HTMLDivElement | null) => void;
}

function AppHeader({ tier2Ref }: AppHeaderProps): ReactNode {
    const { t } = useTranslation(['nav_menu']);

    const [isLogin, setIsLogin] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [login, setLogin] = useState<string>('');
    const [missionPlannerUrl, setMissionPlannerUrl] = useState<string>('');
    const [menuOpen, setMenuOpen] = useState<boolean>(false);

    const access = useAccess();

    // Наличие сессии знает AuthContext: он спрашивает /api/auth/me, а этот
    // эндпоинт интерцептор считает auth-пробой и на 401 никуда не уводит.
    const { user, isLoading: isAuthLoading } = useAuth();

    useEffect(() => {
        // Гостю /user/start не задаём вовсе. Сервер отвечает на него 401, а
        // 401 вне auth-проб интерцептор трактует как протухшую сессию и делает
        // window.location.href = '/login' (axiosInstance.ts). Шапка стоит на
        // всех экранах, включая лендинг, — гостя выбрасывало на вход ещё до
        // того, как он успевал что-нибудь увидеть.
        if (isAuthLoading || !user) {
            setIsLogin(false);
            return;
        }

        APIRequestGet('/user/start', {
            schema: UserSchema,
            logSuccess: true
        }).then((response) => {
            setIsLogin(true);
            setLogin(response.data.login);
            setIsAdmin(response.data.isAdmin);
            if (response.data.missionPlannerUrl) {
                setMissionPlannerUrl(response.data.missionPlannerUrl);
            }
        }).catch(() => {
            // Сессия была, но /user/start её не признал — показываем вход.
            setIsLogin(false);
        });
    }, [user, isAuthLoading]);

    const sections = visibleSections({ isAdmin: isLogin && isAdmin }, access.adminLinks);

    const closeMenu = (): void => setMenuOpen(false);

    return (
        <header className='app-header'>
            <div className='app-header-tier1'>
                <Link className='app-header-brand' to='/' onClick={closeMenu}>
                    {/* Импорт .svg даёт URL, а не компонент. */}
                    <img className='app-header-logo' src={LogoUrl} alt='Air-Link' />
                </Link>

                <button
                    type='button'
                    className='app-header-burger'
                    aria-expanded={menuOpen}
                    aria-controls='app-header-collapse'
                    aria-label={t('menu')}
                    onClick={() => setMenuOpen((open) => !open)}
                >
                    <span className='app-header-burger-bar' />
                    <span className='app-header-burger-bar' />
                    <span className='app-header-burger-bar' />
                </button>

                {/* Навигация и пользовательский блок сворачиваются вместе: по
                    отдельности правый блок переполнял бы шапку на узком экране.
                    На десктопе обёртка — display: contents, то есть оба потомка
                    остаются прямыми элементами флекс-строки этажа. */}
                <div
                    id='app-header-collapse'
                    className={'app-header-collapse' + (menuOpen ? ' app-header-collapse-open' : '')}
                >
                <nav className='app-header-nav'>
                    {sections.map((section) => section.labelKey ? (
                        <NavDropdown
                            key={section.id}
                            id={'app-header-dropdown-' + section.id}
                            title={t(section.labelKey)}
                            menuVariant='dark'
                        >
                            {section.links.map((link) => (
                                <NavDropdown.Item
                                    key={link.to}
                                    as={Link}
                                    to={link.to}
                                    onClick={closeMenu}
                                >
                                    {t(link.labelKey)}
                                </NavDropdown.Item>
                            ))}
                        </NavDropdown>
                    ) : (
                        section.links.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === '/'}
                                onClick={closeMenu}
                                className={({ isActive }) =>
                                    'app-header-link' + (isActive ? ' app-header-link-active' : '')
                                }
                            >
                                {t(link.labelKey)}
                            </NavLink>
                        ))
                    ))}
                </nav>

                <div className='app-header-right'>
                    {isLogin ? (
                        <UserActions
                            login={login}
                            missionPlannerUrl={missionPlannerUrl}
                            onNavigate={closeMenu}
                        />
                    ) : (
                        <>
                            {/* Функция, а не строка: иначе на /login и
                                /registration активный пункт не подсвечивается. */}
                            <NavLink
                                to='/registration'
                                onClick={closeMenu}
                                className={({ isActive }) =>
                                    'app-header-link' + (isActive ? ' app-header-link-active' : '')
                                }
                            >
                                {t('registration')}
                            </NavLink>
                            <NavLink
                                to='/login'
                                onClick={closeMenu}
                                className={({ isActive }) =>
                                    'app-header-link' + (isActive ? ' app-header-link-active' : '')
                                }
                            >
                                {t('sign_in')}
                            </NavLink>
                        </>
                    )}
                </div>
                </div>
            </div>

            {/* Второй этаж: пустой, пока экран ничего в него не положил. */}
            <div className='app-header-tier2' ref={tier2Ref} />
        </header>
    );
}

export default AppHeader;
